import { useEffect } from 'react';
import isEqual from 'react-fast-compare';
import { QueryModel } from '../DataTableQuery.types';
import { useDataTableContext } from '../../DataTableContext';
import { useDeepCompare } from '../../../../../hooks';
import { removeEmptyValuesFromObjectDeep } from '../../../../../utils/recordHelpers';

// Handle query changes from outside of the component
export function useQueryChanges(
  applyChanges: (nextQuery: Partial<QueryModel>, options: { mode: 'patch' | 'replace'; persist: boolean }) => void,
  internalQuery: QueryModel | null | undefined,
  enabled: boolean
) {
  const { querySettings } = useDataTableContext();
  const query = querySettings?.query;

  const queryHasChanged = useDeepCompare(query);

  useEffect(() => {
    // Do nothing if the useQueryChanges is not enabled (due the fact that initialQuery was not yet set)
    // or if the internal QueryModel object is not yet set
    // or query provided by the user is not set - it means we are expecting no external changes
    if (!enabled || isNotSet(internalQuery) || isNotSet(query)) return;

    const isSameQuery = isEqual(removeEmptyValuesFromObjectDeep(query), removeEmptyValuesFromObjectDeep(internalQuery));
    if (queryHasChanged && !isSameQuery) {
      applyChanges(query, { mode: 'replace', persist: true });
    }
  }, [enabled, applyChanges, query, internalQuery, queryHasChanged]);
}

function isNotSet(value: unknown): value is null | undefined {
  return value === null || typeof value === 'undefined';
}
